import { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, getDocs, deleteDoc, doc, updateDoc,
  serverTimestamp, orderBy, query
} from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import {
  HeartHandshake, Upload, Trash2, Eye, MapPin, Users,
  AlertTriangle, X, Pencil, Check, IndianRupee
} from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append("file",          file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder",        "htst_campaigns");
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST", body: formData
  });
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

const EMPTY_FORM = {
  title:"", summary:"", story:"", location:"",
  goalAmount:"", upiId:"", contactPhone:"", urgent:false,
  accountName:"", accountNumber:"", ifscCode:"", bankName:""
};

export default function CampaignManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [selected,  setSelected]  = useState(null);   // preview modal
  const [editing,   setEditing]   = useState(null);   // id being quick-edited
  const [editVals,  setEditVals]  = useState({});     // { raisedAmount, donorCount, accountName, accountNumber, ifscCode, bankName }
  const fileRef = useRef(null);

  useEffect(() => { loadCampaigns(); }, []);

  async function loadCampaigns() {
    try {
      const snap = await getDocs(query(collection(db, "campaigns"), orderBy("createdAt", "desc")));
      setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image file");
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    setPreview({ file, url: URL.createObjectURL(file) });
  }

  async function handleCreate() {
    if (!preview?.file)   return toast.error("Please select a cover image");
    if (!form.title)      return toast.error("Please enter a title");
    if (!form.goalAmount) return toast.error("Please enter a target amount");

    setUploading(true);
    try {
      const imageUrl = await uploadToCloudinary(preview.file);
      await addDoc(collection(db, "campaigns"), {
        title:        form.title,
        summary:      form.summary,
        story:        form.story,
        location:     form.location,
        upiId:        form.upiId,
        contactPhone: form.contactPhone,
        urgent:       form.urgent,
        goalAmount:   Number(form.goalAmount) || 0,
        raisedAmount: 0,
        donorCount:   0,
        imageUrl,
        active:       true,
        createdAt:    serverTimestamp(),
        // Bank details
        accountName:   form.accountName,
        accountNumber: form.accountNumber,
        ifscCode:      form.ifscCode,
        bankName:      form.bankName,
      });
      toast.success("Campaign published! 🎉");
      setForm(EMPTY_FORM);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      loadCampaigns();
    } catch(e) {
      console.error(e);
      toast.error("Failed: " + e.message);
    } finally { setUploading(false); }
  }

  async function toggleField(campaign, field) {
    try {
      const newVal = !campaign[field];
      await updateDoc(doc(db, "campaigns", campaign.id), { [field]: newVal });
      setCampaigns(p => p.map(c => c.id === campaign.id ? { ...c, [field]: newVal } : c));
    } catch(e) {
      toast.error("Failed to update");
    }
  }

  function startEdit(c) {
    setEditing(c.id);
    setEditVals({
      raisedAmount:  c.raisedAmount || 0,
      donorCount:    c.donorCount || 0,
      accountName:   c.accountName || "",
      accountNumber: c.accountNumber || "",
      ifscCode:      c.ifscCode || "",
      bankName:      c.bankName || "",
    });
  }

  async function saveEdit(id) {
    try {
      const raisedAmount = Number(editVals.raisedAmount) || 0;
      const donorCount   = Number(editVals.donorCount) || 0;
      const payload = {
        raisedAmount,
        donorCount,
        accountName:   editVals.accountName || "",
        accountNumber: editVals.accountNumber || "",
        ifscCode:      editVals.ifscCode || "",
        bankName:      editVals.bankName || "",
      };
      await updateDoc(doc(db, "campaigns", id), payload);
      setCampaigns(p => p.map(c => c.id === id ? { ...c, ...payload } : c));
      setEditing(null);
      toast.success("Progress updated!");
    } catch(e) {
      toast.error("Failed to update");
    }
  }

  async function handleDelete(c) {
    if (!window.confirm(`Delete "${c.title}"?\nThis removes it from the public site immediately.`)) return;
    try {
      await deleteDoc(doc(db, "campaigns", c.id));
      setCampaigns(p => p.filter(x => x.id !== c.id));
      toast.success("Campaign deleted");
    } catch(e) { toast.error("Failed to delete"); }
  }

  function pct(c) {
    if (!c.goalAmount) return 0;
    return Math.min(100, Math.round(((c.raisedAmount || 0) / c.goalAmount) * 100));
  }

  return (
    <div>
      <Toaster position="top-center"/>

      {/* Header */}
      <div style={s.header}>
        <HeartHandshake size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Campaign Manager</h2>
          <p style={s.sub}>Start relief campaigns and update progress as donations come in</p>
        </div>
      </div>

      <div style={s.grid2}>
        {/* ── Create Form ── */}
        <div style={s.card}>
          <div style={s.cardTitle}><Upload size={15} color="#f59e0b"/> Start New Campaign</div>

          {/* Cover Image */}
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            style={{
              ...s.dropZone,
              borderColor: preview ? "#f59e0b" : "rgba(255,255,255,0.1)",
              background:  preview ? "rgba(245,158,11,0.04)" : "rgba(255,255,255,0.02)",
              cursor: uploading ? "not-allowed" : "pointer",
            }}>
            {preview ? (
              <div style={{ position:"relative" }}>
                <img src={preview.url} alt="Preview" style={{ width:"100%", maxHeight:180, objectFit:"cover", borderRadius:8 }}/>
                <button
                  onClick={e => { e.stopPropagation(); setPreview(null); if(fileRef.current) fileRef.current.value=""; }}
                  style={s.removeBtn}>
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <div style={{ textAlign:"center", padding:"28px 20px" }}>
                <Upload size={32} color="#52504d" style={{ marginBottom:10 }}/>
                <div style={{ fontSize:13, fontWeight:600, color:"#78716c" }}>Click to select cover image</div>
                <div style={{ fontSize:11, color:"#3a3835", marginTop:2 }}>JPG, PNG, WEBP · Max 10MB</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display:"none" }}/>

          {/* Fields */}
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:14 }}>
            <Field label="Campaign Title *" value={form.title} onChange={v=>set("title",v)}
              placeholder="Nepal Disaster Relief Fund"/>
            <Field label="Short Summary (shown on card)" value={form.summary} onChange={v=>set("summary",v)}
              placeholder="150+ lives lost, 400 missing — help families survive"/>
            <div>
              <label style={s.label}>Full Story (shown when opened)</label>
              <textarea value={form.story} onChange={e=>set("story",e.target.value)}
                placeholder="Give the full context — what happened, who's affected, how funds will be used..."
                rows={4} style={{ ...s.input, resize:"vertical" }}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Field label="Location" value={form.location} onChange={v=>set("location",v)} placeholder="Nepal"/>
              <Field label="Target Amount (₹) *" value={form.goalAmount} onChange={v=>set("goalAmount",v)}
                placeholder="500000" type="number"/>
            </div>

            <div style={s.sectionDivider}>Payment Details</div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Field label="UPI ID (optional)" value={form.upiId} onChange={v=>set("upiId",v)} placeholder="htst@upi"/>
              <Field label="Contact Phone (optional)" value={form.contactPhone} onChange={v=>set("contactPhone",v)} placeholder="+91 XXXXX XXXXX"/>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Field label="Account Holder Name (optional)" value={form.accountName} onChange={v=>set("accountName",v)} placeholder="HELP THEM SHINE TRUST"/>
              <Field label="Bank Name (optional)" value={form.bankName} onChange={v=>set("bankName",v)} placeholder="HDFC Bank Ltd."/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <Field label="Account Number (optional)" value={form.accountNumber} onChange={v=>set("accountNumber",v)} placeholder="50200052424207"/>
              <Field label="IFSC Code (optional)" value={form.ifscCode} onChange={v=>set("ifscCode",v)} placeholder="HDFC0000153"/>
            </div>

            <label style={s.checkboxRow}>
              <input type="checkbox" checked={form.urgent} onChange={e=>set("urgent",e.target.checked)} style={{ width:16, height:16 }}/>
              <span style={{ fontSize:13, color:"#a8a29e" }}>Mark as <strong style={{ color:"#ef4444" }}>Urgent</strong> (shows red badge)</span>
            </label>
          </div>

          <button onClick={handleCreate} disabled={uploading} style={{ ...s.publishBtn, opacity:uploading?0.6:1 }}>
            <HeartHandshake size={16}/> {uploading ? "Publishing..." : "Publish Campaign"}
          </button>
        </div>

        {/* ── Live Campaigns ── */}
        <div style={s.card}>
          <div style={s.cardTitle}>
            <Eye size={15} color="#f59e0b"/> All Campaigns
            <span style={s.badge}>{campaigns.length} total</span>
          </div>

          {loading ? (
            <div style={s.empty}>Loading...</div>
          ) : campaigns.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0" }}>
              <HeartHandshake size={40} color="#1a1714" style={{ marginBottom:12 }}/>
              <div style={{ fontSize:13, color:"#52504d" }}>No campaigns yet</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:12, maxHeight:640, overflowY:"auto" }}>
              {campaigns.map(c => (
                <div key={c.id} style={s.campRow}>
                  <div style={{ display:"flex", gap:12 }}>
                    <img src={c.imageUrl} alt={c.title}
                      onClick={() => setSelected(c)}
                      style={{ width:64, height:64, objectFit:"cover", borderRadius:9, flexShrink:0, cursor:"pointer" }}
                      onError={e=>e.target.style.display="none"}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#fef3e2" }}>{c.title}</div>
                        {c.urgent && <span style={s.urgentPill}><AlertTriangle size={9}/> Urgent</span>}
                        {c.active === false && <span style={s.inactivePill}>Inactive</span>}
                      </div>
                      {c.location && (
                        <div style={{ fontSize:11, color:"#52504d", display:"flex", alignItems:"center", gap:4, marginTop:3 }}>
                          <MapPin size={10}/> {c.location}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Progress */}
                  <div style={s.progressTrack}>
                    <div style={{ ...s.progressFill, width:`${pct(c)}%` }}/>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#78716c", marginBottom:8 }}>
                    <span>₹{(c.raisedAmount||0).toLocaleString()} of ₹{(c.goalAmount||0).toLocaleString()}</span>
                    <span>{pct(c)}% funded</span>
                  </div>

                  {/* Quick edit raised/donors + bank details */}
                  {editing === c.id ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:10 }}>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <div style={{ position:"relative", flex:1 }}>
                          <IndianRupee size={11} color="#52504d" style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)" }}/>
                          <input type="number" value={editVals.raisedAmount}
                            onChange={e=>setEditVals(v=>({...v, raisedAmount:e.target.value}))}
                            style={{ ...s.miniInput, paddingLeft:22 }} placeholder="Raised"/>
                        </div>
                        <div style={{ position:"relative", width:90 }}>
                          <Users size={11} color="#52504d" style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)" }}/>
                          <input type="number" value={editVals.donorCount}
                            onChange={e=>setEditVals(v=>({...v, donorCount:e.target.value}))}
                            style={{ ...s.miniInput, paddingLeft:22 }} placeholder="Donors"/>
                        </div>
                      </div>

                      <div style={s.miniSectionLabel}>Bank Details</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                        <input value={editVals.accountName}
                          onChange={e=>setEditVals(v=>({...v, accountName:e.target.value}))}
                          style={s.miniInput} placeholder="Account Name"/>
                        <input value={editVals.bankName}
                          onChange={e=>setEditVals(v=>({...v, bankName:e.target.value}))}
                          style={s.miniInput} placeholder="Bank Name"/>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                        <input value={editVals.accountNumber}
                          onChange={e=>setEditVals(v=>({...v, accountNumber:e.target.value}))}
                          style={s.miniInput} placeholder="Account Number"/>
                        <input value={editVals.ifscCode}
                          onChange={e=>setEditVals(v=>({...v, ifscCode:e.target.value}))}
                          style={s.miniInput} placeholder="IFSC Code"/>
                      </div>

                      <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                        <button onClick={()=>saveEdit(c.id)} style={s.saveIconBtn}><Check size={14}/></button>
                        <button onClick={()=>setEditing(null)} style={s.cancelIconBtn}><X size={14}/></button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={()=>startEdit(c)} style={s.updateProgressBtn}>
                      <Pencil size={12}/> Update Progress & Bank Details
                    </button>
                  )}

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                    <button onClick={()=>toggleField(c,"active")} style={{
                      ...s.actionBtn,
                      color: c.active===false ? "#22c55e" : "#f59e0b",
                      borderColor: c.active===false ? "rgba(34,197,94,0.3)" : "rgba(245,158,11,0.3)",
                      background: c.active===false ? "rgba(34,197,94,0.08)" : "rgba(245,158,11,0.08)",
                    }}>
                      {c.active===false ? "Reactivate" : "Deactivate"}
                    </button>
                    <button onClick={()=>toggleField(c,"urgent")} style={{
                      ...s.actionBtn,
                      color: c.urgent ? "#78716c" : "#ef4444",
                      borderColor: c.urgent ? "rgba(255,255,255,0.1)" : "rgba(239,68,68,0.3)",
                      background: c.urgent ? "rgba(255,255,255,0.03)" : "rgba(239,68,68,0.08)",
                    }}>
                      {c.urgent ? "Unmark Urgent" : "Mark Urgent"}
                    </button>
                    <button onClick={()=>handleDelete(c)} style={{ ...s.actionBtn, color:"#ef4444", borderColor:"rgba(239,68,68,0.3)", background:"rgba(239,68,68,0.08)", marginLeft:"auto" }}>
                      <Trash2 size={12}/> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} style={s.closeBtn}><X size={18}/></button>
            <img src={selected.imageUrl} alt={selected.title}
              style={{ width:"100%", maxHeight:"55vh", objectFit:"cover", borderRadius:12, marginBottom:16 }}/>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:800, color:"#fef3e2", margin:"0 0 8px" }}>
              {selected.title}
            </h3>
            {selected.summary && <p style={{ fontSize:13, color:"#a8a29e", marginBottom:12 }}>{selected.summary}</p>}
            {selected.story && <p style={{ fontSize:13, color:"#78716c", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{selected.story}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type="text" }) {
  return (
    <div>
      <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
    </div>
  );
}

const s = {
  header:       { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  title:        { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub:          { fontSize:13, color:"#78716c", margin:0 },
  grid2:        { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" },
  card:         { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 },
  cardTitle:    { fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:16, display:"flex", alignItems:"center", gap:7 },
  badge:        { marginLeft:"auto", fontSize:10, background:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.25)", padding:"2px 8px", borderRadius:50 },
  dropZone:     { border:"2px dashed", borderRadius:12, overflow:"hidden", transition:"all 0.2s", minHeight:110 },
  removeBtn:    { position:"absolute", top:8, right:8, background:"rgba(0,0,0,0.7)", border:"none", borderRadius:"50%", width:26, height:26, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#fff" },
  label:        { display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:5 },
  input:        { width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  sectionDivider: { fontSize:11, fontWeight:800, color:"#52504d", textTransform:"uppercase", letterSpacing:"1px", borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:14, marginTop:2 },
  checkboxRow:  { display:"flex", alignItems:"center", gap:8, cursor:"pointer" },
  publishBtn:   { width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:11, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit", marginTop:16 },
  empty:        { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
  campRow:      { background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, padding:14 },
  urgentPill:   { display:"inline-flex", alignItems:"center", gap:3, fontSize:9, fontWeight:800, color:"#ef4444", background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.25)", padding:"2px 7px", borderRadius:50, textTransform:"uppercase" },
  inactivePill: { fontSize:9, fontWeight:800, color:"#78716c", background:"rgba(255,255,255,0.06)", padding:"2px 7px", borderRadius:50, textTransform:"uppercase" },
  progressTrack:{ height:7, background:"rgba(255,255,255,0.06)", borderRadius:50, overflow:"hidden", marginTop:10, marginBottom:5 },
  progressFill: { height:"100%", background:"linear-gradient(90deg,#f59e0b,#fbbf24)", borderRadius:50, transition:"width 0.4s" },
  miniInput:    { width:"100%", padding:"7px 8px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.1)", borderRadius:7, color:"#fef3e2", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  miniSectionLabel: { fontSize:10, fontWeight:800, color:"#52504d", textTransform:"uppercase", letterSpacing:"0.5px", marginTop:4 },
  saveIconBtn:  { width:30, height:30, borderRadius:7, background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.3)", color:"#22c55e", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  cancelIconBtn:{ width:30, height:30, borderRadius:7, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", color:"#78716c", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  updateProgressBtn: { display:"inline-flex", alignItems:"center", gap:6, background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.25)", color:"#3b82f6", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  actionBtn:    { display:"inline-flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:8, border:"1px solid", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  overlay:      { position:"fixed", inset:0, background:"rgba(0,0,0,0.9)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(10px)" },
  modal:        { background:"#111009", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:24, width:"100%", maxWidth:560, position:"relative", maxHeight:"90vh", overflowY:"auto" },
  closeBtn:     { position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%", width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#a8a29e", zIndex:1 },
};